import * as moment from 'moment';

import {ReferralPayoutStatusEnum} from '@spa/api';

import {
    ComponentTypesEnum,
    UnspecifiedKeysEnum,
} from '../../../app/shared/enums';
import {
    ReportTableColumn,
    ReportTableConfigInterface,
} from '../../../app/shared/components';
import {FilterControlInterface} from '../../../app/shared/interfaces';
import {
    BaseControl,
    DatepickerControl,
    SelectControl,
} from '../../../app/shared/classes';

import {environment} from '../../../environments/environment';

/**
 * Filter control names of the Payout Requests report. They are form control names only - the
 * request is mapped field by field in the component, so they are free to stay camelCase while the
 * wire stays snake_case.
 */
export const ReferralPayoutReportAffiliateSelector = 'referralPayoutAffiliate';
export const ReferralPayoutReportDateTypeSelector = 'referralPayoutDateType';
export const ReferralPayoutReportDateFromSelector = 'referralPayoutDateFrom';
export const ReferralPayoutReportDateToSelector = 'referralPayoutDateTo';
export const ReferralPayoutReportStatusSelector = 'referralPayoutStatus';
export const ReferralPayoutReportQuickSearchSelector = 'referralPayoutQuickSearch';

/**
 * Server defaults of `POST referral/reports/referral-payouts` - the default order column is
 * `requested_at` (newest first) and the default page size is 50 (server accepts 1-200).
 */
export const ReferralPayoutReportDefaultOrderColumn = 'requested_at';
export const ReferralPayoutReportDefaultPageSize = 50;

/**
 * Deep-link query parameter carrying a numeric referral profile id. When present on navigation
 * the report pre-filters by it (`affiliate_profile_ids`) and auto-submits.
 */
export const ReferralPayoutReportAffiliateQueryParam = 'affiliate';

/** Largest page size the report endpoint accepts - used for the CSV export request. */
export const ReferralPayoutReportMaxPageSize = 200;

/**
 * Which payout date column the Date From/To pair filters. The values are the wire order columns of
 * the matching date range, so they double as the request field selectors.
 */
export enum ReferralPayoutReportDateTypesEnum {
    RequestedDate = 'requested_at',
    ApprovedDate = 'approved_at',
    PaidDate = 'paid_at'
}

/**
 * Column selectors - the snake_case field names of ReferralPayoutReportRowInterface.
 */
export const ReferralPayoutReportColumnNames = {
    Id: 'id',
    Affiliate: 'affiliate_email',
    RequestedAt: 'requested_at',
    TotalsFormatted: 'totals_formatted',
    EntryCount: 'entry_count',
    Status: 'status',
    ApprovedAt: 'approved_at',
    PaidAt: 'paid_at',
    PaymentReference: 'payment_reference',
    ApprovedBy: 'approved_by_operator_user_id'
};

/**
 * Filter block of the report. The two datepickers open pre-filled with today as a DRAFT only -
 * the page loads the full list first and the drafts are applied once the operator submits, the
 * same draft/applied split the referred-accounts report uses.
 */
export const PanelReportsReferralPayoutsFilterControls = (): FilterControlInterface[] => {
    const today = moment().format(environment.dateFormatLocalDate);

    return [
        {
            name: ReferralPayoutReportAffiliateSelector,
            placeholder: 'Affiliate (code or email)',
            controls: [
                new BaseControl({
                    name: ReferralPayoutReportAffiliateSelector,
                    value: null,
                    placeholder: 'Affiliate (code or email)',
                    validators: [],
                    componentType: ComponentTypesEnum.Text
                })
            ],
            shown: true,
            sortIndex: 1
        },
        {
            name: ReferralPayoutReportDateTypeSelector,
            placeholder: 'Date Type',
            controls: [
                new SelectControl({
                    name: ReferralPayoutReportDateTypeSelector,
                    value: ReferralPayoutReportDateTypesEnum.RequestedDate,
                    placeholder: 'Date Type',
                    validators: [],
                    componentType: ComponentTypesEnum.Select,
                    options: [
                        {label: 'Request Date', value: ReferralPayoutReportDateTypesEnum.RequestedDate},
                        {label: 'Approved Date', value: ReferralPayoutReportDateTypesEnum.ApprovedDate},
                        {label: 'Paid Date', value: ReferralPayoutReportDateTypesEnum.PaidDate}
                    ]
                }),
                new DatepickerControl({
                    name: ReferralPayoutReportDateFromSelector,
                    value: today,
                    placeholder: 'Date From',
                    maxDate: today,
                    validators: [],
                    componentType: ComponentTypesEnum.Datepicker,
                    sortIndex: 2
                }),
                new DatepickerControl({
                    name: ReferralPayoutReportDateToSelector,
                    value: today,
                    placeholder: 'Date To',
                    maxDate: today,
                    validators: [],
                    componentType: ComponentTypesEnum.Datepicker,
                    sortIndex: 3
                })
            ],
            shown: true,
            sortIndex: 2
        },
        {
            name: ReferralPayoutReportStatusSelector,
            placeholder: 'Status',
            controls: [
                new SelectControl({
                    name: ReferralPayoutReportStatusSelector,
                    value: UnspecifiedKeysEnum.UnspecifiedKey,
                    placeholder: 'Status',
                    validators: [],
                    componentType: ComponentTypesEnum.Select,
                    options: [
                        {label: 'All', value: UnspecifiedKeysEnum.UnspecifiedKey},
                        {label: 'Requested', value: ReferralPayoutStatusEnum.Requested},
                        {label: 'Approved', value: ReferralPayoutStatusEnum.Approved},
                        {label: 'Paid', value: ReferralPayoutStatusEnum.Paid},
                        {label: 'Rejected', value: ReferralPayoutStatusEnum.Rejected},
                        {label: 'Cancelled', value: ReferralPayoutStatusEnum.Cancelled}
                    ]
                })
            ],
            shown: true,
            sortIndex: 3
        },
        {
            name: ReferralPayoutReportQuickSearchSelector,
            placeholder: 'Quick Search',
            controls: [
                new BaseControl({
                    name: ReferralPayoutReportQuickSearchSelector,
                    value: null,
                    placeholder: 'Quick Search',
                    validators: [],
                    componentType: ComponentTypesEnum.Text
                })
            ],
            shown: true,
            sortIndex: 4
        }
    ];
};

/**
 * One row per payout request. The Request ID cell is rendered clickable by the component and
 * opens the payout request detail - this report also backs the operator payout queue.
 */
export const PanelReportsReferralPayoutsDataTableConfig = (): ReportTableConfigInterface => {
    return {
        rows: [],
        columns: [
            new ReportTableColumn({
                label: 'Request ID',
                selector: ReferralPayoutReportColumnNames.Id
            }),
            new ReportTableColumn({
                label: 'Affiliate',
                selector: ReferralPayoutReportColumnNames.Affiliate
            }),
            new ReportTableColumn({
                label: 'Date Requested',
                selector: ReferralPayoutReportColumnNames.RequestedAt
            }),
            new ReportTableColumn({
                label: 'Total',
                selector: ReferralPayoutReportColumnNames.TotalsFormatted,
                shifted: true
            }),
            new ReportTableColumn({
                label: 'Line Items',
                selector: ReferralPayoutReportColumnNames.EntryCount,
                shifted: true
            }),
            new ReportTableColumn({
                label: 'Status',
                selector: ReferralPayoutReportColumnNames.Status
            }),
            new ReportTableColumn({
                label: 'Date Approved',
                selector: ReferralPayoutReportColumnNames.ApprovedAt
            }),
            new ReportTableColumn({
                label: 'Date Paid',
                selector: ReferralPayoutReportColumnNames.PaidAt
            }),
            new ReportTableColumn({
                label: 'Payment Reference',
                selector: ReferralPayoutReportColumnNames.PaymentReference
            }),
            new ReportTableColumn({
                label: 'Approved By',
                selector: ReferralPayoutReportColumnNames.ApprovedBy,
                shifted: true
            })
        ],
        hiddenColumnIds: [],
        actionMenu: false,
        disableColumnsOptions: true
    };
};
